import { AxiosError } from 'axios'
import { isEmpty } from 'lodash'
import { action, computed, flow, makeObservable, observable } from 'mobx'

import { openPdfDocumentFromBlob, requestWrapper } from 'Shared/utils'

import { STORE_UI } from 'Core/constants/stores'
import { CustomGenerator, LoadingBtnNames } from 'Core/interfaces'
import {
	entityToLabelValue,
	entityToStringLabelValue,
	formatStateProvinceOption,
	getDefaultStoreArrayValue,
	getDefaultStoreValue
} from 'Core/utils'
import { StateProvince } from 'Core/utils/storeHelpers/types'

import {
	getRegistrationDocumentSaveMessage,
	getRegistrationDocumentSaveSuccessMessage,
	getRegistrationSumissionSuccessMessage
} from 'Portals/Supplier/constants/messages'
import { RegistrationDocument, RegistrationDocumentDocumentFields } from 'Portals/Supplier/entities'
import { getSubmissionMessage } from 'Portals/Supplier/Pages/RegistrationDocument/Body/utils/messages'
import { RegistrationDocumentRepo } from 'Portals/Supplier/repositories'
import { RootStore } from 'Portals/Supplier/stores'

export class RegistrationDocumentStore {
	@observable accessor supplierRequest = getDefaultStoreValue<RegistrationDocument.SupplierRequest>()
	@observable accessor stateProvinces = getDefaultStoreArrayValue<StateProvince>()
	@observable accessor services = getDefaultStoreArrayValue<Entity>()
	@observable accessor counties = getDefaultStoreArrayValue<Entity>()
	@observable accessor countries = getDefaultStoreArrayValue<Entity>()
	@observable accessor diversitychoices = getDefaultStoreArrayValue<Entity>()
	@observable accessor legalStructures = getDefaultStoreArrayValue<Entity>()
	@observable accessor certifications = getDefaultStoreArrayValue<Entity>()
	@observable accessor emrReasons = getDefaultStoreArrayValue<Entity>()
	@observable accessor oshaReasons = getDefaultStoreArrayValue<Entity>()
	@observable accessor employmentPolicies = getDefaultStoreArrayValue<Entity>()
	@observable accessor issuesAddressed = getDefaultStoreArrayValue<Entity>()
	@observable accessor csrPrograms = getDefaultStoreArrayValue<Entity>()
	@observable accessor ethicalPolicies = getDefaultStoreArrayValue<Entity>()
	@observable accessor topMessage: React.ReactNode | null = null
	@observable accessor documents = getDefaultStoreArrayValue<RegistrationDocument.Document.Basic>()
	@observable accessor documentTypes = getDefaultStoreArrayValue<Entity>()
	@observable accessor additionalLocations =
		getDefaultStoreArrayValue<RegistrationDocument.AdditionalLocation.Basic>()
	@observable accessor additionalLocation =
		getDefaultStoreValue<RegistrationDocument.AdditionalLocation.Detailed>()
	@observable accessor isDocumentSectionValid: boolean = false

	constructor(public rootStore: RootStore) {
	}

	@action.bound
	setTopMessage(message: React.ReactNode): void {
		this.topMessage = message
	}

	@computed
	get documentTypeOptions(): LabelValue[] {
		const uploadedTypes = this.documents.value.map(
			(document) => document[RegistrationDocumentDocumentFields.documentType]?.id
		)

		return this.documentTypes.value.reduce<LabelValue[]>(
			(acc, documentType) =>
				uploadedTypes.includes(documentType.id)
					? acc
					: [...acc, entityToStringLabelValue(documentType)],
			[]
		)
	}

	@flow.bound
	*retrieveSupplierRequest(): CustomGenerator<RegistrationDocument.SupplierRequest> {
		try {
			yield requestWrapper({
				field: this.supplierRequest,
				repository: RegistrationDocumentRepo.retrieveSupplierRequest,
				repositoryParams: null
			})
		} catch {
			this.rootStore[STORE_UI].showErrorNotification()
		}
	}

	@flow.bound
	*updateAdditionalLocation(
		locationId: Key,
		data: RegistrationDocument.AdditionalLocation.Form.Request
	) {
		const id = this.supplierRequest.value?.id

		if (!id || !locationId || !data) {
			return
		}

		this.rootStore[STORE_UI].setIsRequestSubmitting()

		try {
			yield RegistrationDocumentRepo.updateAdditionalLocation(id, locationId, data)

			this.retrieveAdditionalLocations()
			this.rootStore[STORE_UI].closeModal()
		} catch (error) {
			this.rootStore[STORE_UI].showErrorNotification(error as AxiosError)
		} finally {
			this.rootStore[STORE_UI].setIsRequestSubmitting(false)
		}
	}

	@flow.bound
	*addAdditionalLocation(data: RegistrationDocument.AdditionalLocation.Form.Request) {
		const id = this.supplierRequest.value?.id

		if (!id || !data) {
			return
		}

		this.rootStore[STORE_UI].setIsRequestSubmitting()

		try {
			yield RegistrationDocumentRepo.addAdditionalLocation(id, data)

			this.retrieveAdditionalLocations()
			this.rootStore[STORE_UI].closeModal()
		} catch (error) {
			this.rootStore[STORE_UI].showErrorNotification(error as AxiosError)
		} finally {
			this.rootStore[STORE_UI].setIsRequestSubmitting(false)
		}
	}

	@flow.bound
	*deleteAdditionalLocation(locationId: Key): CustomGenerator<void> {
		const id = this.supplierRequest.value?.id

		if (!id || !locationId) {
			return
		}

		this.rootStore[STORE_UI].setIsRequestSubmitting()

		try {
			yield RegistrationDocumentRepo.deleteAdditionalLocation(id, locationId)
			this.retrieveAdditionalLocations()
		} catch (error) {
			this.rootStore[STORE_UI].showErrorNotification(error as AxiosError)
		} finally {
			this.rootStore[STORE_UI].setIsRequestSubmitting(false)
		}
	}

	@flow.bound
	*retrieveAdditionalLocation(
		locationId: Key
	): CustomGenerator<RegistrationDocument.AdditionalLocation.Detailed> {
		const id = this.supplierRequest.value?.id

		if (!id) {
			return
		}

		yield requestWrapper({
			field: this.additionalLocation,
			repository: RegistrationDocumentRepo.retrieveAdditionalLocation,
			repositoryParams: { id, locationId }
		})
	}

	@flow.bound
	*retrieveAdditionalLocations(): CustomGenerator<
		RegistrationDocument.AdditionalLocation.Basic[]
	> {
		const id = this.supplierRequest.value?.id

		if (!id) {
			return
		}

		yield requestWrapper({
			field: this.additionalLocations,
			repository: RegistrationDocumentRepo.retrieveAdditionalLocations,
			repositoryParams: id
		})
	}

	@flow.bound
	*removeDocument(documentId: Key): CustomGenerator<void> {
		const id = this.supplierRequest.value?.id

		if (!id) {
			return
		}

		this.rootStore[STORE_UI].setIsBtnLoading(LoadingBtnNames.deleteDocument)

		try {
			yield RegistrationDocumentRepo.removeDocument(id, documentId)
			this.retrieveDocuments()
		} catch (error) {
			this.rootStore[STORE_UI].showErrorNotification(error as AxiosError)
		} finally {
			this.rootStore[STORE_UI].setIsBtnLoading(null, false)
		}
	}

	@flow.bound
	*downloadDocument(documentId: Key, documentName: string): CustomGenerator<Blob> {
		const id = this.supplierRequest.value?.id

		if (!id) {
			return
		}

		try {
			const document = yield RegistrationDocumentRepo.downloadDocument(id, documentId)

			openPdfDocumentFromBlob(document, documentName)
		} catch (error) {
			this.rootStore[STORE_UI].showErrorNotification(error as AxiosError)
		}
	}

	@flow.bound
	*retrieveDocumentTypes(): CustomGenerator<Entity[]> {
		yield requestWrapper({
			field: this.documentTypes,
			repository: RegistrationDocumentRepo.retrieveDocumentTypes,
			repositoryParams: null
		})
	}

	@flow.bound
	*retrieveDocuments(): CustomGenerator<RegistrationDocument.Document.Basic[]> {
		const id = this.supplierRequest.value?.id

		if (!id) {
			return
		}

		yield requestWrapper({
			field: this.documents,
			repository: RegistrationDocumentRepo.retrieveDocuments,
			repositoryParams: id
		})
	}

	@flow.bound
	*uploadDocument(data: RegistrationDocument.Document.Request) {
		const id = this.supplierRequest.value?.id

		if (!id) {
			return
		}

		this.rootStore[STORE_UI].setIsRequestSubmitting()

		try {
			yield RegistrationDocumentRepo.uploadDocument(id, data)

			this.rootStore[STORE_UI].showSuccessNotification(getRegistrationDocumentSaveMessage())
			this.retrieveDocuments()
			this.rootStore[STORE_UI].closeModal()
		} catch (error) {
			this.rootStore[STORE_UI].showErrorNotification(error as AxiosError)
		} finally {
			this.rootStore[STORE_UI].setIsRequestSubmitting(false)
		}
	}

	@flow.bound
	*retrieveStateProvinces(): CustomGenerator<StateProvince[]> {
		yield requestWrapper({
			field: this.stateProvinces,
			repository: RegistrationDocumentRepo.retrieveStateProvinces,
			repositoryParams: null
		})
	}

	@flow.bound
	*retrieveCounties(): CustomGenerator<Entity[]> {
		yield requestWrapper({
			field: this.counties,
			repository: RegistrationDocumentRepo.retrieveCounties,
			repositoryParams: null
		})
	}

	@flow.bound
	*retrieveCountries(): CustomGenerator<Entity[]> {
		yield requestWrapper({
			field: this.countries,
			repository: RegistrationDocumentRepo.retrieveCountries,
			repositoryParams: null
		})
	}

	@flow.bound
	*retrieveServices(): CustomGenerator<Entity[]> {
		yield requestWrapper({
			field: this.services,
			repository: RegistrationDocumentRepo.retrieveServices,
			repositoryParams: null
		})
	}

	@flow.bound
	*retrieveDiversityChoices(): CustomGenerator<Entity[]> {
		yield requestWrapper({
			field: this.diversitychoices,
			repository: RegistrationDocumentRepo.retrieveDiversityChoices,
			repositoryParams: null
		})
	}

	@flow.bound
	*retrieveLegalStructures(): CustomGenerator<Entity[]> {
		yield requestWrapper({
			field: this.legalStructures,
			repository: RegistrationDocumentRepo.retrieveLegalStructures,
			repositoryParams: null
		})
	}

	@flow.bound
	*retrieveCertifications(): CustomGenerator<Entity[]> {
		yield requestWrapper({
			field: this.certifications,
			repository: RegistrationDocumentRepo.retrieveCertifications,
			repositoryParams: null
		})
	}

	@flow.bound
	*retrieveEmrReasons(): CustomGenerator<Entity[]> {
		yield requestWrapper({
			field: this.emrReasons,
			repository: RegistrationDocumentRepo.retrieveEmrReasons,
			repositoryParams: null
		})
	}

	@flow.bound
	*retrieveOshaReasons(): CustomGenerator<Entity[]> {
		yield requestWrapper({
			field: this.oshaReasons,
			repository: RegistrationDocumentRepo.retrieveOshaReasons,
			repositoryParams: null
		})
	}

	@flow.bound
	*retrieveEmploymentPolicies(): CustomGenerator<Entity[]> {
		yield requestWrapper({
			field: this.employmentPolicies,
			repository: RegistrationDocumentRepo.retrieveEmploymentPolicies,
			repositoryParams: null
		})
	}

	@flow.bound
	*retrieveIssuesAddressed(): CustomGenerator<Entity[]> {
		yield requestWrapper({
			field: this.issuesAddressed,
			repository: RegistrationDocumentRepo.retrieveIssuesAddressed,
			repositoryParams: null
		})
	}

	@flow.bound
	*retrieveCsrPrograms(): CustomGenerator<Entity[]> {
		yield requestWrapper({
			field: this.csrPrograms,
			repository: RegistrationDocumentRepo.retrieveCsrPrograms,
			repositoryParams: null
		})
	}

	@flow.bound
	*retrieveEthicalPolicies(): CustomGenerator<Entity[]> {
		yield requestWrapper({
			field: this.ethicalPolicies,
			repository: RegistrationDocumentRepo.retrieveEthicalPolicies,
			repositoryParams: null
		})
	}

	@computed
	get isRegistationDocumentStatusRejected() {
		return this.supplierRequest.value?.supplierRequestStatus?.name === 'Rejected'
	}

	@computed
	get stateProvinceOptions() {
		return this.stateProvinces.value.map(formatStateProvinceOption)
	}

	@computed
	get countryOptions() {
		return this.countries.value.map(entityToLabelValue)
	}

	@computed
	get countyOptions() {
		return this.counties.value.map(entityToLabelValue)
	}

	@computed
	get serviceOptions() {
		return this.services.value.map(entityToLabelValue)
	}

	@computed
	get diversityChoicesOptions() {
		return this.diversitychoices.value.map(entityToLabelValue)
	}

	@computed
	get legalStructuresOptions() {
		return this.legalStructures.value.map(entityToLabelValue)
	}

	@computed
	get certificationsOptions() {
		return this.certifications.value.map(entityToLabelValue)
	}

	@computed
	get emrReasonsOptions() {
		return this.emrReasons.value.map(entityToLabelValue)
	}

	@computed
	get oshaReasonsOptions() {
		return this.oshaReasons.value.map(entityToLabelValue)
	}

	@computed
	get employmentPolicyOptions() {
		return this.employmentPolicies.value.map(entityToLabelValue)
	}

	@computed
	get issuesAddressedOptions() {
		return this.issuesAddressed.value.map(entityToLabelValue)
	}

	@computed
	get csrProgramsOptions() {
		return this.csrPrograms.value.map(entityToLabelValue)
	}

	@computed
	get ethicalPoliciesOptions() {
		return this.ethicalPolicies.value.map(entityToLabelValue)
	}

	@computed
	get isRegistationDocumentEditable() {
		return this.supplierRequest.value?.supplierRequestStatus?.name !== 'Approved'
	}

	@flow.bound
	*retrieveOptions() {
		try {
			yield this.retrieveCounties()
			this.retrieveServices()
			this.retrieveStateProvinces()
			this.retrieveDiversityChoices()
			this.retrieveLegalStructures()
			this.retrieveCertifications()
			this.retrieveEmrReasons()
			this.retrieveOshaReasons()
			this.retrieveEmploymentPolicies()
			this.retrieveIssuesAddressed()
			this.retrieveCsrPrograms()
			this.retrieveEthicalPolicies()
		} catch (error) {
			this.rootStore[STORE_UI].showErrorNotification()
		}
	}

	@flow.bound
	*retrieveAdditionalLocationData(id?: Key) {
		try {
			if (id) {
				yield this.retrieveAdditionalLocation(id)
			}

			this.retrieveCounties()
			this.retrieveCountries()
			this.retrieveStateProvinces()
		} catch (error) {
			this.rootStore[STORE_UI].showErrorNotification()
		}
	}

	@computed
	get isPageLoading() {
		return (
			this.supplierRequest.isLoading ||
			isEmpty(this.stateProvinceOptions) ||
			isEmpty(this.countyOptions) ||
			isEmpty(this.serviceOptions) ||
			isEmpty(this.diversityChoicesOptions) ||
			isEmpty(this.legalStructuresOptions) ||
			isEmpty(this.csrProgramsOptions) ||
			isEmpty(this.ethicalPoliciesOptions)
		)
	}

	@flow.bound
	*saveRegistrationDocument(payload: RegistrationDocument.Request): CustomGenerator<void> {
		if (!this.supplierRequest.value) {
			return
		}

		this.rootStore[STORE_UI].setIsBtnLoading(LoadingBtnNames.save)

		const registrationDocumentId = this.supplierRequest.value.id

		try {
			yield RegistrationDocumentRepo.saveRegistrationDocument(registrationDocumentId, payload)
			this.rootStore[STORE_UI].showSuccessNotification(
				getRegistrationDocumentSaveSuccessMessage()
			)
		} catch (error) {
			this.rootStore[STORE_UI].showErrorNotification(error as AxiosError)
		} finally {
			this.rootStore[STORE_UI].setIsBtnLoading(null, false)
		}
	}

	@flow.bound
	*submitRegistrationDocument(payload: RegistrationDocument.Request): CustomGenerator<void> {
		if (!this.supplierRequest.value) {
			return
		}
		const registrationDocumentId = this.supplierRequest.value.id

		this.rootStore[STORE_UI].setIsBtnLoading(LoadingBtnNames.submit)

		try {
			yield RegistrationDocumentRepo.submitRegistrationDocument(
				registrationDocumentId,
				payload
			)
			this.rootStore[STORE_UI].closeModal()

			this.retrieveSupplierRequest()

			this.setTopMessage(getSubmissionMessage())

			this.rootStore[STORE_UI].showSuccessNotification(
				getRegistrationSumissionSuccessMessage()
			)
		} catch (error) {
			this.rootStore[STORE_UI].showErrorNotification(error as AxiosError)
		} finally {
			this.rootStore[STORE_UI].setIsBtnLoading(null, false)
		}
	}

	@action.bound
	setIsDocumentSectionValid(value: boolean): void {
		this.isDocumentSectionValid = value
	}

	@action.bound
	clearDocumentTypes() {
		this.documentTypes = getDefaultStoreArrayValue<Entity>()
	}

	@action.bound
	clearAdditionalLocationData() {
		this.additionalLocation =
			getDefaultStoreValue<RegistrationDocument.AdditionalLocation.Detailed>()
	}
}
